import { useState, useEffect } from 'react';
import { authService } from '@/services/authService';
import { resolveUserType } from '@/lib/livePlanAccess';

export type SubscriptionPlan = 'freemium' | 'porto2life' | 'live';

interface AuthState {
  isAuthenticated: boolean;
  userType: string | null;
  userName: string | null;
  userId: number | null;
  user: any | null;
  userPhoto: string | null;
  subscriptionPlan: SubscriptionPlan | null;
  loading: boolean;
}

export const useAuthState = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    userType: null,
    userName: null,
    userId: null,
    user: null,
    userPhoto: null,
    subscriptionPlan: null,
    loading: true,
  });

  useEffect(() => {
    const checkAuthState = () => {
      try {
        const authData = authService.getAuthData();
        
        // Validação adicional: verificar se todos os dados necessários estão presentes
        const isValidSession = authData.isAuthenticated && 
                              authData.token && 
                              authData.user && 
                              authData.userType && 
                              authData.userId;
        
        console.log('🔍 Verificando sessão:', {
          isAuthenticated: authData.isAuthenticated,
          hasToken: !!authData.token,
          hasUser: !!authData.user,
          userType: authData.userType,
          userId: authData.userId,
          isValidSession
        });
        
        const resolvedUserType = isValidSession
          ? resolveUserType(authData.userType, authData.user)
          : null;

        setAuthState({
          isAuthenticated: isValidSession,
          userType: resolvedUserType,
          userName: isValidSession ? authData.user?.name || null : null,
          userId: isValidSession ? authData.userId : null,
          user: isValidSession ? authData.user : null,
          userPhoto: isValidSession ? authData.user?.profile_photo || null : null,
          subscriptionPlan: isValidSession ? (authData.user?.subscription_plan ?? 'freemium') : null,
          loading: false,
        });
      } catch (error) {
        console.error('❌ Erro ao verificar estado de autenticação:', error);
        setAuthState({
          isAuthenticated: false,
          userType: null,
          userName: null,
          userId: null,
          user: null,
          userPhoto: null,
          subscriptionPlan: null,
          loading: false,
        });
      }
    };

    // Aguardar um pouco para garantir que o localStorage foi carregado
    const timer = setTimeout(checkAuthState, 100);
    
    // Listener para mudanças no localStorage
    const handleStorageChange = () => {
      checkAuthState();
    };

    // Listener para eventos customizados de atualização de usuário
    const handleUserUpdate = () => {
      console.log('🔄 Evento de atualização de usuário recebido');
      checkAuthState();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleUserUpdate);
    
    // Verificar a cada 5 segundos se o token ainda é válido
    const interval = setInterval(() => {
      const token = localStorage.getItem('authToken');
      if (token && !authService.isTokenValid(token)) {
        authService.logout();
        checkAuthState();
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate);
      clearInterval(interval);
    };
  }, []);

  const logout = () => {
    console.log('🚪 Fazendo logout...');
    authService.logout();
    setAuthState({
      isAuthenticated: false,
      userType: null,
      userName: null,
      userId: null,
      user: null,
      userPhoto: null,
      subscriptionPlan: null,
      loading: false,
    });
  };

  const clearSession = () => {
    console.log('🧹 Limpando sessão completamente...');
    // Limpar localStorage completamente
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    
    // Resetar estado
    setAuthState({
      isAuthenticated: false,
      userType: null,
      userName: null,
      userId: null,
      user: null,
      userPhoto: null,
      subscriptionPlan: null,
      loading: false,
    });
  };

  const refreshUser = async () => {
    if (authState.userId) {
      try {
        const updatedUser = await authService.getUserById(authState.userId);
        if (updatedUser) {
          if (updatedUser.user_type) {
            localStorage.setItem('userType', updatedUser.user_type);
          }
          setAuthState(prev => ({
            ...prev,
            user: updatedUser,
            userName: updatedUser.name,
            userPhoto: updatedUser.profile_photo,
            userType: updatedUser.user_type ?? prev.userType,
            subscriptionPlan: updatedUser.subscription_plan ?? prev.subscriptionPlan,
          }));
        }
      } catch (error) {
        console.error('Erro ao atualizar dados do usuário:', error);
      }
    }
  };

  return {
    ...authState,
    logout,
    clearSession,
    refreshUser,
  };
};
